import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import NotFound from "../pages/NotFound";
import { AdminGuard } from "../components/feature/AdminGuard";
import { AuthGuard } from "../components/feature/AuthGuard";

const Home = lazy(() => import("../pages/home/page"));
const ArtistProfile = lazy(() => import("../pages/artist/page"));
const PrefecturePage = lazy(() => import("../pages/prefecture/page"));
const DirectoryPage = lazy(() => import("../pages/directory/page"));
const AdminPage = lazy(() => import("../pages/admin/page"));
const AdminShopsPage = lazy(() => import("../pages/admin/shops/page"));
const AdminGenresPage = lazy(() => import("../pages/admin/genres/page"));
const GenresHubPage = lazy(() => import("../pages/admin/genres/hub/page"));
const AdminGenresShopsPage = lazy(() => import("../pages/admin/genres/shops/page"));
const AdminGenresEventsPage = lazy(() => import("../pages/admin/genres/events/page"));
const AdminSoundElementsPage = lazy(() => import("../pages/admin/sound-elements/page"));
const AdminProductionRatesPage = lazy(() => import("../pages/admin/production-rates/page"));
const AdminDesignFeePage = lazy(() => import("../pages/admin/design-fee/page"));
const LoginPage = lazy(() => import("../pages/login/page"));
const ProfileSetupPage = lazy(() => import("../pages/profile-setup/page"));
const LayerEditPage = lazy(() => import("../pages/layer-edit/page"));
const EventPage = lazy(() => import("../pages/event/page"));
const AdminEventsPage = lazy(() => import("../pages/admin/events/page"));
const AdminArtistsPage = lazy(() => import("../pages/admin/artists/page"));
const FavoritesPage = lazy(() => import("../pages/favorites/page"));
const NFCRedirectPage = lazy(() => import("../pages/nfc/page"));
const TopPage = lazy(() => import("../pages/top/page"));
const TimelinePage = lazy(() => import("../pages/timeline/page"));
const AnalyticsPage = lazy(() => import("../pages/analytics/page"));
const ShopProfileEditPage = lazy(() => import("../pages/shop-profile-edit/page"));
const NodeEventCreatePage = lazy(() => import("../pages/node-event-create/page"));
const NodeAnalyticsPage = lazy(() => import("../pages/node-analytics/page"));
const GenreArtistsPage = lazy(() => import("../pages/genre-artists/page"));
const GenreEventsPage = lazy(() => import("../pages/genre-events/page"));
const ArtistsAnalyticsPage = lazy(() => import("../pages/admin/artists-analytics/page"));
const ShopDetail = lazy(() => import("../pages/shop/page"));
const UserManagementPage = lazy(() => import("../pages/admin/user-management/page"));
const FinancePage = lazy(() => import("../pages/admin/finance/page"));
const EchoesPage = lazy(() => import("../pages/admin/echoes/page"));
const AuditLogsPage = lazy(() => import("../pages/admin/audit-logs/page"));
const LegalNoticePage = lazy(() => import("../pages/legal/LegalNoticePage"));
const TermsPage = lazy(() => import("../pages/legal/TermsPage"));
const PrivacyPage = lazy(() => import("../pages/legal/PrivacyPage"));
const MerchandisePage = lazy(() => import("../pages/merchandise/page"));
const AdminMerchandisePage = lazy(() => import("../pages/admin/merchandise/page"));
const CommissionManagementPage = lazy(() => import("../pages/admin/merchandise/commission/page"));
const PlatformOrdersPage = lazy(() => import("../pages/admin/merchandise/platform-orders/page"));
const ResonateHistoryPage = lazy(() => import("../pages/resonate-history/page"));
const ResonateReceivedPage = lazy(() => import("../pages/resonate-received/page"));
const ExplorePage = lazy(() => import("../pages/explore/page"));
const SubscriptionPage = lazy(() => import("../pages/subscription/page"));
const DivePage = lazy(() => import("../pages/dive/page"));
const MypagePage = lazy(() => import("../pages/mypage/page"));
const AdminContactsPage = lazy(() => import("../pages/admin/contacts/page"));
const ReceptionPage = lazy(() => import("../pages/reception/page"));
const GuidePage = lazy(() => import("../pages/guide/page"));
const ContactPage = lazy(() => import("../pages/contact/page"));
const ResonanceCardPage = lazy(() => import("../pages/resonance-card/page"));
const RankingPage = lazy(() => import("../pages/ranking/page"));
const MerchandiseProductionRequestPage = lazy(() => import("../pages/merchandise-production-request/page"));
const TroubleReportsPage = lazy(() => import("../pages/admin/merchandise/trouble-reports/page"));
const GoodbyePage = lazy(() => import("../pages/goodbye/page"));
const AdminNFCPatternsPage = lazy(() => import("../pages/admin/nfc-patterns/page"));
const AdminNFCManagementPage = lazy(() => import("../pages/admin/nfc-management/page"));
const ScanPage = lazy(() => import("../pages/scan/page"));
const AdminPickupPage = lazy(() => import("../pages/admin/pickup/page"));

const routes: RouteObject[] = [
  { path: "/", element: <Home /> },
  { path: "/login", element: <LoginPage /> },
  // ── 一般ユーザー向けログイン必須ルート（AuthGuard で保護） ──
  { 
    path: "/profile-setup", 
    element: <AuthGuard><ProfileSetupPage /></AuthGuard> 
  },
  { path: "/profile-edit", element: <Navigate to="/mypage" replace /> },
  { 
    path: "/layer-edit", 
    element: <AuthGuard><LayerEditPage /></AuthGuard> 
  },
  { path: "/artist/:id", element: <ArtistProfile /> },
  { path: "/artist/u/:userId", element: <ArtistProfile /> },
  { path: "/directory", element: <DirectoryPage /> },
  { path: "/favorites", element: <FavoritesPage /> },
  { path: "/inventory", element: <Navigate to="/mypage" replace /> },
  { path: "/prefecture/nara", element: <PrefecturePage /> },
  { path: "/event/:id", element: <EventPage /> },
  { path: "/shop/u/:userId", element: <ShopDetail /> },
  { path: "/shop/:id", element: <ShopDetail /> },
  { path: "/nfc", element: <NFCRedirectPage /> },
  { path: "/top", element: <TopPage /> },
  { path: "/timeline", element: <TimelinePage /> },
  { path: "/explore", element: <ExplorePage /> },
  { path: "/ranking", element: <RankingPage /> },
  { 
    path: "/mypage", 
    element: <AuthGuard><MypagePage /></AuthGuard> 
  },
  { 
    path: "/analytics", 
    element: <AuthGuard><AnalyticsPage /></AuthGuard> 
  },
  { 
    path: "/shop-profile-edit", 
    element: <AuthGuard><ShopProfileEditPage /></AuthGuard> 
  },
  { 
    path: "/node-event-create", 
    element: <AuthGuard><NodeEventCreatePage /></AuthGuard> 
  },
  { 
    path: "/node-analytics", 
    element: <AuthGuard><NodeAnalyticsPage /></AuthGuard> 
  },
  { path: "/genre-artists", element: <GenreArtistsPage /> },
  { path: "/genre-events", element: <GenreEventsPage /> },
  { path: "/merchandise", element: <MerchandisePage /> },
  {
    path: '/merchandise/production-request',
    element: (
      <AuthGuard>
        <MerchandiseProductionRequestPage />
      </AuthGuard>
    ),
  },
  { path: "/resonate-history", element: <Navigate to="/mypage" replace /> },
  { 
    path: "/resonate-received", 
    element: <AuthGuard><ResonateReceivedPage /></AuthGuard> 
  },
  { 
    path: "/subscription", 
    element: <AuthGuard><SubscriptionPage /></AuthGuard> 
  },
  { path: "/dive", element: <DivePage /> },
  { path: "/scan", element: <ScanPage /> },
  // ── スタッフ受付専用ルート（ログイン不要） ──
  { path: "/reception/:eventId", element: <ReceptionPage /> },
  // ── 法的ページ ──
  { path: "/legal-notice", element: <LegalNoticePage /> },
  { path: "/terms", element: <TermsPage /> },
  { path: "/privacy", element: <PrivacyPage /> },
  // ── 管理者専用ルート（AdminGuard で保護） ──
  {
    path: "/admin",
    element: <AdminGuard><AdminPage /></AdminGuard>,
  },
  {
    path: "/admin/shops",
    element: <AdminGuard><AdminShopsPage /></AdminGuard>,
  },
  {
    path: "/admin/events",
    element: <AdminGuard><AdminEventsPage /></AdminGuard>,
  },
  {
    path: "/admin/artists",
    element: <AdminGuard><AdminArtistsPage /></AdminGuard>,
  },
  {
    path: "/admin/artists/analytics",
    element: <AdminGuard><ArtistsAnalyticsPage /></AdminGuard>,
  },
  {
    path: "/admin/genres",
    element: <AdminGuard><GenresHubPage /></AdminGuard>,
  },
  {
    path: "/admin/genres/artists",
    element: <AdminGuard><AdminGenresPage /></AdminGuard>,
  },
  {
    path: "/admin/genres/shops",
    element: <AdminGuard><AdminGenresShopsPage /></AdminGuard>,
  },
  {
    path: "/admin/genres/events",
    element: <AdminGuard><AdminGenresEventsPage /></AdminGuard>,
  },
  {
    path: "/admin/sound-elements",
    element: <AdminGuard><AdminSoundElementsPage /></AdminGuard>,
  },
  {
    path: "/admin/user-management",
    element: <AdminGuard><UserManagementPage /></AdminGuard>,
  },
  {
    path: "/admin/finance",
    element: <AdminGuard><FinancePage /></AdminGuard>,
  },
  {
    path: "/admin/echoes",
    element: <AdminGuard><EchoesPage /></AdminGuard>,
  },
  {
    path: "/admin/audit-logs",
    element: <AdminGuard><AuditLogsPage /></AdminGuard>,
  },
  {
    path: "/admin/merchandise",
    element: <AdminGuard><AdminMerchandisePage /></AdminGuard>,
  },
  {
    path: "/admin/merchandise/commission",
    element: <AdminGuard><CommissionManagementPage /></AdminGuard>,
  },
  {
    path: "/admin/merchandise/platform-orders",
    element: <AdminGuard><PlatformOrdersPage /></AdminGuard>,
  },
  {
    path: "/admin/merchandise/trouble-reports",
    element: <AdminGuard><TroubleReportsPage /></AdminGuard>,
  },
  {
    path: "/admin/production-rates",
    element: <AdminGuard><AdminProductionRatesPage /></AdminGuard>,
  },
  {
    path: "/admin/design-fee",
    element: <AdminGuard><AdminDesignFeePage /></AdminGuard>,
  },
  {
    path: "/admin/contacts",
    element: (
      <AdminGuard>
        <AdminContactsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/nfc-patterns",
    element: (
      <AdminGuard>
        <AdminNFCPatternsPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/nfc-management",
    element: (
      <AdminGuard>
        <AdminNFCManagementPage />
      </AdminGuard>
    ),
  },
  {
    path: "/admin/pickup",
    element: (
      <AdminGuard>
        <AdminPickupPage />
      </AdminGuard>
    ),
  },
  {
    path: "/goodbye",
    element: <GoodbyePage />,
  },
  { path: "*", element: <NotFound /> },
  {
    path: "/guide",
    element: <GuidePage />,
  },
  {
    path: "/contact",
    element: <ContactPage />,
  },
  {
    path: "/resonance-card",
    element: <AuthGuard><ResonanceCardPage /></AuthGuard>,
  },
  {
    path: '/merchandise',
    element: (
      <AuthGuard>
        <MerchandisePage />
      </AuthGuard>
    ),
  },
];

export default routes;